import { t } from "@lingui/macro"
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Stack,
    Tooltip,
} from "@mui/material"
import {
    ExpandMore,
    Code,
    Message,
    SmartToy,
    AccountTree,
    TrendingUp,
    Assessment,
} from "@mui/icons-material"

import { IChatMessage } from "src/types/chat-message/chatMessage"
import { chatbot_NodeMetadata } from "src/api/models/chatbot_NodeMetadata"
import { chatbot_MessageInfoSource } from "src/api/models/chatbot_MessageInfoSource"
import { getAgentDisplayName, getAgentColor } from "src/lib/agent-display"
import { formatDuration } from "src/helpers/duration"

interface IPromptsSectionProps {
    message: IChatMessage
    _handleSourceClick?: (item?: chatbot_MessageInfoSource) => void
}

const PromptsSection = ({
    message,
    _handleSourceClick,
}: IPromptsSectionProps) => {
    const getNodeDisplayName = (node: chatbot_NodeMetadata) => {
        if (node.node_name != null && node.node_name !== "") {
            const nodeNameMap: Record<string, string> = {
                GetDataNode: "Get Data Node",
                ExpandInputNode: "Expand Input Node",
                NoAnswerNode: "No Answer Node",
                MapReplyNode: "Map Reply Node",
                EnoughInfoNode: "Enough Info Node",
                StoreResponseNode: "Store Response Node",
                SupervisorNode: "Supervisor Node",
                SupervisorNodeV2: "Supervisor V2 Node",
                HasDataDecisionNode: "Has Data Decision Node",
                EnoughInfoDecisionNode: "Enough Info Decision Node",
                DecideErrandClassifiedNode: "Decide Errand Classified Node",
                AddConversationHistoryNode: "Add Conversation History Node",
                StoreUserInputNode: "Store User Input Node",
                SelectFormTypeNode: "Select Form Type Node",
                SubmitMessageNode: "Submit Message Node",
                EditNode: "Edit Node",
                AddActionsNode: "Add Actions Node",
                StoreLastActionNode: "Store Last Action Node",
                GetNextFieldNode: "Get Next Field Node",
                AttemptFieldAnswerNode: "Attempt Field Answer Node",
                SendNextQuestionNode: "Send Next Question Node",
                ConfirmSubmitNode: "Confirm Submit Node",
                SubmitFormNode: "Submit Form Node",
                ThanksForFillingOutFormNode: "Thanks For Filling Out Form Node",
                NotAnswerNode: "Not Answer Node",
                DispatchDecisionNode: "Dispatch Decision Node",
                ClarifyQuestionWithDataNode: "Clarify Question With Data Node",
                ClarifyQuestionForDispatchNode:
                    "Clarify Question For Dispatch Node",
                ConfirmContactDetailsNode: "Confirm Contact Details Node",
                ErrandNeedsMoreInfoNode: "Errand Needs More Info Node",
                MapClassificationReplyNode: "Map Classification Reply Node",
            }

            const nodeName = node.node_name
            if (nodeName != null && nodeNameMap[nodeName] !== undefined) {
                return nodeNameMap[nodeName]
            }

            if (nodeName != null) {
                let displayName = nodeName
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())
                    .trim()
                    .replace(/\s+/g, " ")

                return displayName
            }
        }
        return ""
    }

    const formatPrompt = (prompt: string) => {
        return prompt.split("\n").map((line, index) => (
            <span key={index}>
                {line}
                <br />
            </span>
        ))
    }

    const formatResponse = (response: string) => {
        const trimmedResponse = response.trim()
        if (/^\d+$/.test(trimmedResponse)) {
            const num = parseInt(trimmedResponse, 10)
            const incrementedNum = num + 1
            return (
                <span>
                    {incrementedNum}
                    <br />
                </span>
            )
        }

        return response.split("\n").map((line, index) => (
            <span key={index}>
                {line}
                <br />
            </span>
        ))
    }

    const formatConfidenceScore = (confidence: number) => {
        return `${Math.round(confidence * 100)}%`
    }

    const hasSupervisorV2State = (node: chatbot_NodeMetadata) => {
        return (
            node.node_name === "SupervisorNodeV2" &&
            Boolean(node.state?.confidence_scores) &&
            Array.isArray(node.state.confidence_scores) &&
            node.state.confidence_scores.length > 0
        )
    }

    const getNodeTooltip = (nodeName: string) => {
        const tooltipMap: Record<string, string> = {
            GetDataNode: t`chat-message-journey.node-tooltips.get-data-node`,
            ExpandInputNode: t`chat-message-journey.node-tooltips.expand-input-node`,
            NoAnswerNode: t`chat-message-journey.node-tooltips.no-answer-node`,
            MapReplyNode: t`chat-message-journey.node-tooltips.map-reply-node`,
            EnoughInfoNode: t`chat-message-journey.node-tooltips.enough-info-node`,
            StoreResponseNode: t`chat-message-journey.node-tooltips.store-response-node`,
            SupervisorNode: t`chat-message-journey.node-tooltips.supervisor-node`,
            SupervisorNodeV2: t`chat-message-journey.node-tooltips.supervisor-v2-node`,
            HasDataDecisionNode: t`chat-message-journey.node-tooltips.has-data-decision-node`,
            EnoughInfoDecisionNode: t`chat-message-journey.node-tooltips.enough-info-decision-node`,
            DecideErrandClassifiedNode: t`chat-message-journey.node-tooltips.decide-errand-classified-node`,
            AddConversationHistoryNode: t`chat-message-journey.node-tooltips.add-conversation-history-node`,
            StoreUserInputNode: t`chat-message-journey.node-tooltips.store-user-input-node`,
            SelectFormTypeNode: t`chat-message-journey.node-tooltips.select-form-type-node`,
            SubmitMessageNode: t`chat-message-journey.node-tooltips.submit-message-node`,
            EditNode: t`chat-message-journey.node-tooltips.edit-node`,
            AddActionsNode: t`chat-message-journey.node-tooltips.add-actions-node`,
            StoreLastActionNode: t`chat-message-journey.node-tooltips.store-last-action-node`,
            GetNextFieldNode: t`chat-message-journey.node-tooltips.get-next-field-node`,
            AttemptFieldAnswerNode: t`chat-message-journey.node-tooltips.attempt-field-answer-node`,
            SendNextQuestionNode: t`chat-message-journey.node-tooltips.send-next-question-node`,
            ConfirmSubmitNode: t`chat-message-journey.node-tooltips.confirm-submit-node`,
            SubmitFormNode: t`chat-message-journey.node-tooltips.submit-form-node`,
            ThanksForFillingOutFormNode: t`chat-message-journey.node-tooltips.thanks-for-filling-out-form-node`,
            NotAnswerNode: t`chat-message-journey.node-tooltips.not-answer-node`,
            DispatchDecisionNode: t`chat-message-journey.node-tooltips.dispatch-decision-node`,
            ClarifyQuestionWithDataNode: t`chat-message-journey.node-tooltips.clarify-question-with-data-node`,
            ClarifyQuestionForDispatchNode: t`chat-message-journey.node-tooltips.clarify-question-for-dispatch-node`,
            ConfirmContactDetailsNode: t`chat-message-journey.node-tooltips.confirm-contact-details-node`,
            ErrandNeedsMoreInfoNode: t`chat-message-journey.node-tooltips.errand-needs-more-info-node`,
            MapClassificationReplyNode: t`chat-message-journey.node-tooltips.map-classification-reply-node`,
        }

        return (
            tooltipMap[nodeName] ??
            t`chat-message-journey.node-tooltips.default`
        )
    }

    const getNodeType = (nodeName: string) => {
        const lowerName = nodeName.toLowerCase()

        if (lowerName.includes("decision") || lowerName.includes("decide")) {
            return "Decision"
        } else {
            return "State"
        }
    }

    const getNodeTypeTooltip = (nodeType: string) => {
        switch (nodeType) {
            case "Decision":
                return t`chat-message-journey.node-type-tooltips.decision`
            case "State":
                return t`chat-message-journey.node-type-tooltips.state`
            default:
                return t`chat-message-journey.node-type-tooltips.default`
        }
    }

    const getNodeColor = (nodeType: string) => {
        switch (nodeType) {
            case "Decision":
                return "secondary"
            case "State":
                return "primary"
            default:
                return "default"
        }
    }

    const getAgentInfo = () => {
        const allNodesData = message?.nodeInfo ?? []
        const uniqueAgents = new Set<string>()

        for (const node of allNodesData) {
            if (node.agent_name != null && node.agent_name !== "") {
                uniqueAgents.add(String(node.agent_name))
            }
        }
        return Array.from(uniqueAgents)
    }

    const getAgentIcon = (agentName: string) => {
        if (agentName === "supervisor") {
            return <AccountTree sx={{ fontSize: "18px" }} />
        }
        return <SmartToy sx={{ fontSize: "18px" }} />
    }

    const getNodesByAgent = () => {
        const allNodesData = message?.nodeInfo ?? []
        const nodesByAgent: Record<string, chatbot_NodeMetadata[]> = {}

        allNodesData.forEach((node) => {
            const agentName = node.agent_name ?? "unknown"
            if (nodesByAgent[agentName] === undefined) {
                nodesByAgent[agentName] = []
            }
            nodesByAgent[agentName].push(node)
        })

        return nodesByAgent
    }

    const getValidNodesByAgent = () => {
        const nodesByAgent = getNodesByAgent()
        const validNodesByAgent: Record<string, chatbot_NodeMetadata[]> = {}

        Object.entries(nodesByAgent).forEach(([agentName, nodes]) => {
            const validNodes = nodes.filter((node) => {
                const displayName = getNodeDisplayName(node)
                return displayName !== "" && displayName != null
            })
            if (validNodes.length > 0) {
                validNodesByAgent[agentName] = validNodes
            }
        })

        return validNodesByAgent
    }

    const getPromptsByAgent = () => {
        const nodesByAgent = getNodesByAgent()
        const promptsByAgent: Record<string, chatbot_NodeMetadata[]> = {}

        Object.entries(nodesByAgent).forEach(([agentName, nodes]) => {
            const promptNodes = nodes.filter((node) => {
                if (node?.raw_prompt !== undefined && node?.raw_prompt !== "") {
                    return true
                }
                if (
                    node?.filled_prompt !== undefined &&
                    node?.filled_prompt !== ""
                ) {
                    return true
                }
                if (node?.prompt !== undefined && node?.prompt !== "") {
                    return true
                }
                if (node?.prompts !== undefined && node?.prompts.length > 0) {
                    return true
                }
                return false
            })

            if (promptNodes.length > 0) {
                promptsByAgent[agentName] = promptNodes
            }
        })

        return promptsByAgent
    }

    return (
        <Box>
            {(() => {
                const agentInfo = getAgentInfo()
                const validNodesByAgent = getValidNodesByAgent()
                const promptsByAgent = getPromptsByAgent()

                if (agentInfo.length === 0) {
                    return null
                }

                return agentInfo.map((agentName, agentIndex) => {
                    const displayName = getAgentDisplayName(agentName)
                    const agentColor = getAgentColor(agentName)
                    const agentIcon = getAgentIcon(agentName)
                    const validNodes = validNodesByAgent[agentName] ?? []
                    const promptNodes = promptsByAgent[agentName] ?? []

                    return (
                        <Box key={agentIndex}>
                            {agentIndex === 0 ? (
                                <Box
                                    sx={{
                                        height: "1px",
                                        bgcolor: "grey.200",
                                        mb: 3,
                                    }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        height: "1px",
                                        bgcolor: "grey.200",
                                        my: 3,
                                    }}
                                />
                            )}

                            <Box sx={{ mb: 4 }}>
                                <Box
                                    component="section"
                                    sx={{ marginBottom: 2 }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ marginBottom: 0.5 }}
                                    >
                                        Agent
                                    </Typography>
                                    <Box
                                        sx={{
                                            bgcolor: agentColor,
                                            color: "white",
                                            padding: "5px",
                                            borderRadius: 1,
                                            display: "inline-flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {agentIcon}
                                        <Typography
                                            sx={{
                                                py: 0,
                                                px: 0.5,
                                                fontSize: "14px",
                                                fontWeight: 500,
                                            }}
                                        >
                                            {displayName}
                                        </Typography>
                                    </Box>
                                </Box>

                                {validNodes.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ marginBottom: 0.5 }}
                                        >
                                            {t`chat-message-journey.node-journey`}
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            sx={{ flexWrap: "wrap", gap: 1 }}
                                        >
                                            {validNodes.map((node, index) => {
                                                const nodeType = getNodeType(
                                                    node.node_name ?? "",
                                                )
                                                const nodeColor =
                                                    getNodeColor(nodeType)
                                                const displayName =
                                                    getNodeDisplayName(node)
                                                const tooltip = getNodeTooltip(
                                                    node.node_name ?? "",
                                                )

                                                return (
                                                    <Box key={index}>
                                                        <Tooltip
                                                            title={
                                                                <Box>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontWeight: 600,
                                                                            mb: 1,
                                                                        }}
                                                                    >
                                                                        {
                                                                            displayName
                                                                        }
                                                                    </Typography>
                                                                    <Typography variant="body2">
                                                                        {
                                                                            tooltip
                                                                        }
                                                                    </Typography>
                                                                </Box>
                                                            }
                                                            placement="top"
                                                            arrow
                                                        >
                                                            <Chip
                                                                label={`${
                                                                    index + 1
                                                                }. ${displayName}`}
                                                                color={
                                                                    nodeColor
                                                                }
                                                                variant="outlined"
                                                                size="small"
                                                                sx={{
                                                                    mb: 1,
                                                                    cursor: "help",
                                                                }}
                                                            />
                                                        </Tooltip>

                                                        {hasSupervisorV2State(
                                                            node,
                                                        ) && (
                                                            <Box
                                                                sx={{
                                                                    mt: 2,
                                                                    ml: 1,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    gutterBottom
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                        gap: 1,
                                                                        mb: 2,
                                                                    }}
                                                                >
                                                                    <Assessment />
                                                                    Agent Scores
                                                                </Typography>

                                                                {node.state?.confidence_scores?.map(
                                                                    (
                                                                        score: {
                                                                            agent_id: string
                                                                            confidence: number
                                                                            reasoning: string
                                                                        },
                                                                        scoreIndex: number,
                                                                    ) => (
                                                                        <Box
                                                                            key={
                                                                                scoreIndex
                                                                            }
                                                                            sx={{
                                                                                mb: 2,
                                                                                p: 2,
                                                                                bgcolor:
                                                                                    "grey.50",
                                                                                borderRadius: 1,
                                                                                border: "2px solid",
                                                                                borderColor:
                                                                                    score.agent_id ===
                                                                                    node
                                                                                        .state
                                                                                        ?.selected_agent
                                                                                        ? getAgentColor(
                                                                                              score.agent_id,
                                                                                          )
                                                                                        : "grey.200",
                                                                                boxShadow:
                                                                                    score.agent_id ===
                                                                                    node
                                                                                        .state
                                                                                        ?.selected_agent
                                                                                        ? `0 0 0 1px ${getAgentColor(
                                                                                              score.agent_id,
                                                                                          )}`
                                                                                        : "none",
                                                                            }}
                                                                        >
                                                                            <Box
                                                                                sx={{
                                                                                    display:
                                                                                        "flex",
                                                                                    justifyContent:
                                                                                        "space-between",
                                                                                    alignItems:
                                                                                        "center",
                                                                                    mb: 1,
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{
                                                                                        fontWeight: 600,
                                                                                    }}
                                                                                >
                                                                                    {getAgentDisplayName(
                                                                                        score.agent_id,
                                                                                    )}
                                                                                </Typography>
                                                                                <Chip
                                                                                    label={formatConfidenceScore(
                                                                                        score.confidence,
                                                                                    )}
                                                                                    sx={{
                                                                                        backgroundColor:
                                                                                            getAgentColor(
                                                                                                score.agent_id,
                                                                                            ),
                                                                                        color: "white",
                                                                                        "& .MuiChip-label":
                                                                                            {
                                                                                                color: "white",
                                                                                            },
                                                                                    }}
                                                                                    size="small"
                                                                                    variant="filled"
                                                                                />
                                                                            </Box>
                                                                            <Typography
                                                                                variant="body2"
                                                                                color="text.secondary"
                                                                                sx={{
                                                                                    fontSize:
                                                                                        "0.875rem",
                                                                                }}
                                                                            >
                                                                                {
                                                                                    score.reasoning
                                                                                }
                                                                            </Typography>
                                                                        </Box>
                                                                    ),
                                                                )}

                                                                {Boolean(
                                                                    node.state
                                                                        ?.confidence_summary,
                                                                ) && (
                                                                    <Box
                                                                        sx={{
                                                                            mt: 2,
                                                                            p: 2,
                                                                            bgcolor:
                                                                                "primary.50",
                                                                            borderRadius: 1,
                                                                            border: "1px solid",
                                                                            borderColor:
                                                                                "primary.200",
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            gutterBottom
                                                                            sx={{
                                                                                display:
                                                                                    "flex",
                                                                                alignItems:
                                                                                    "center",
                                                                                gap: 1,
                                                                                mb: 1,
                                                                            }}
                                                                        >
                                                                            <TrendingUp />
                                                                            Confidence
                                                                            Summary
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontSize:
                                                                                    "0.875rem",
                                                                            }}
                                                                        >
                                                                            {
                                                                                node
                                                                                    .state
                                                                                    ?.confidence_summary
                                                                            }
                                                                        </Typography>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )
                                            })}
                                        </Stack>
                                    </Box>
                                )}

                                {/* Prompts for this agent */}
                                {promptNodes.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ marginBottom: 1 }}
                                        >
                                            {t`chat-message-journey.prompts.nodes-with-prompts`}
                                        </Typography>

                                        {promptNodes.map((node, index) => {
                                            const nodeType = getNodeType(
                                                node.node_name ?? "",
                                            )
                                            const nodeColor =
                                                getNodeColor(nodeType)
                                            const displayName =
                                                getNodeDisplayName(node)

                                            return (
                                                <Accordion
                                                    key={index}
                                                    sx={{ mb: 2 }}
                                                >
                                                    <AccordionSummary
                                                        expandIcon={
                                                            <ExpandMore />
                                                        }
                                                        sx={{
                                                            bgcolor: "grey.50",
                                                            border: "1px solid",
                                                            borderColor:
                                                                "grey.200",
                                                            borderRadius: 1,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 2,
                                                                width: "100%",
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    flex: 1,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    {
                                                                        displayName
                                                                    }
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                >
                                                                    {nodeType}{" "}
                                                                    Node
                                                                </Typography>
                                                            </Box>
                                                            <Stack
                                                                direction="row"
                                                                spacing={1}
                                                            >
                                                                {Boolean(
                                                                    node.prompt_version,
                                                                ) && (
                                                                    <Chip
                                                                        label={`v${node.prompt_version}`}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color={
                                                                            nodeColor
                                                                        }
                                                                    />
                                                                )}
                                                                <Tooltip
                                                                    title={getNodeTypeTooltip(
                                                                        nodeType,
                                                                    )}
                                                                    placement="top"
                                                                    arrow
                                                                >
                                                                    <Chip
                                                                        label={
                                                                            nodeType
                                                                        }
                                                                        size="small"
                                                                        color={
                                                                            nodeColor
                                                                        }
                                                                        sx={{
                                                                            cursor: "help",
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                            </Stack>
                                                        </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails
                                                        sx={{ pt: 2 }}
                                                    >
                                                        {node.duration_ms !=
                                                            null && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    display:
                                                                        "block",
                                                                    mb:
                                                                        node.prompts !=
                                                                            null &&
                                                                        node
                                                                            .prompts
                                                                            .length >
                                                                            0
                                                                            ? 2
                                                                            : 0,
                                                                }}
                                                            >
                                                                {t`chat-message-journey.duration.node`}
                                                                :{" "}
                                                                {formatDuration(
                                                                    node.duration_ms,
                                                                )}
                                                            </Typography>
                                                        )}
                                                        {node.prompts != null &&
                                                        node.prompts.length >
                                                            0 ? (
                                                            <Stack spacing={3}>
                                                                {node.prompts.map(
                                                                    (
                                                                        promptInfo,
                                                                        promptIdx,
                                                                    ) => (
                                                                        <Box
                                                                            key={
                                                                                promptIdx
                                                                            }
                                                                            sx={{
                                                                                borderLeft:
                                                                                    "3px solid",
                                                                                borderColor:
                                                                                    "primary.main",
                                                                                pl: 2,
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="subtitle2"
                                                                                sx={{
                                                                                    mb: 2,
                                                                                    fontWeight: 600,
                                                                                    color: "primary.main",
                                                                                }}
                                                                            >
                                                                                {
                                                                                    promptInfo.name
                                                                                }
                                                                            </Typography>
                                                                            {promptInfo.duration_ms !=
                                                                                null && (
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    color="text.secondary"
                                                                                    sx={{
                                                                                        display:
                                                                                            "block",
                                                                                        mb: 1,
                                                                                    }}
                                                                                >
                                                                                    {`${t`chat-message-journey.duration.llm-call`}: ${formatDuration(
                                                                                        promptInfo.duration_ms,
                                                                                    )}`}
                                                                                </Typography>
                                                                            )}
                                                                            <Stack
                                                                                spacing={
                                                                                    2
                                                                                }
                                                                            >
                                                                                {Boolean(
                                                                                    promptInfo.filled_prompt,
                                                                                ) && (
                                                                                    <Box>
                                                                                        <Typography
                                                                                            variant="subtitle2"
                                                                                            gutterBottom
                                                                                            sx={{
                                                                                                display:
                                                                                                    "flex",
                                                                                                alignItems:
                                                                                                    "center",
                                                                                                gap: 1,
                                                                                            }}
                                                                                        >
                                                                                            <Code />
                                                                                            {t`chat-message-journey.prompts.filled_prompt`}
                                                                                        </Typography>
                                                                                        <Box
                                                                                            sx={{
                                                                                                p: 2,
                                                                                                bgcolor:
                                                                                                    "grey.50",
                                                                                                borderRadius: 1,
                                                                                                fontFamily:
                                                                                                    "monospace",
                                                                                                fontSize:
                                                                                                    "0.875rem",
                                                                                                whiteSpace:
                                                                                                    "pre-wrap",
                                                                                                overflowX:
                                                                                                    "auto",
                                                                                                border: "1px solid",
                                                                                                borderColor:
                                                                                                    "grey.200",
                                                                                                maxHeight:
                                                                                                    "300px",
                                                                                                overflowY:
                                                                                                    "auto",
                                                                                            }}
                                                                                        >
                                                                                            {formatPrompt(
                                                                                                promptInfo.filled_prompt ??
                                                                                                    "",
                                                                                            )}
                                                                                        </Box>
                                                                                    </Box>
                                                                                )}
                                                                                {Boolean(
                                                                                    promptInfo.raw_prompt,
                                                                                ) && (
                                                                                    <Box>
                                                                                        <Typography
                                                                                            variant="subtitle2"
                                                                                            gutterBottom
                                                                                            sx={{
                                                                                                display:
                                                                                                    "flex",
                                                                                                alignItems:
                                                                                                    "center",
                                                                                                gap: 1,
                                                                                            }}
                                                                                        >
                                                                                            <Code />
                                                                                            {t`chat-message-journey.prompts.raw_prompt`}
                                                                                        </Typography>
                                                                                        <Box
                                                                                            sx={{
                                                                                                p: 2,
                                                                                                bgcolor:
                                                                                                    "grey.100",
                                                                                                borderRadius: 1,
                                                                                                fontFamily:
                                                                                                    "monospace",
                                                                                                fontSize:
                                                                                                    "0.875rem",
                                                                                                whiteSpace:
                                                                                                    "pre-wrap",
                                                                                                overflowX:
                                                                                                    "auto",
                                                                                                border: "1px solid",
                                                                                                borderColor:
                                                                                                    "grey.300",
                                                                                                maxHeight:
                                                                                                    "200px",
                                                                                                overflowY:
                                                                                                    "auto",
                                                                                            }}
                                                                                        >
                                                                                            {formatPrompt(
                                                                                                promptInfo.raw_prompt ??
                                                                                                    "",
                                                                                            )}
                                                                                        </Box>
                                                                                    </Box>
                                                                                )}
                                                                                {Boolean(
                                                                                    promptInfo.response,
                                                                                ) && (
                                                                                    <Box>
                                                                                        <Typography
                                                                                            variant="subtitle2"
                                                                                            gutterBottom
                                                                                            sx={{
                                                                                                display:
                                                                                                    "flex",
                                                                                                alignItems:
                                                                                                    "center",
                                                                                                gap: 1,
                                                                                            }}
                                                                                        >
                                                                                            <Message />
                                                                                            {t`chat-message-journey.prompts.response`}
                                                                                        </Typography>
                                                                                        <Box
                                                                                            sx={{
                                                                                                p: 2,
                                                                                                bgcolor:
                                                                                                    "grey.50",
                                                                                                borderRadius: 1,
                                                                                                fontFamily:
                                                                                                    "monospace",
                                                                                                fontSize:
                                                                                                    "0.875rem",
                                                                                                whiteSpace:
                                                                                                    "pre-wrap",
                                                                                                overflowX:
                                                                                                    "auto",
                                                                                                border: "1px solid",
                                                                                                borderColor:
                                                                                                    "grey.200",
                                                                                                maxHeight:
                                                                                                    "200px",
                                                                                                overflowY:
                                                                                                    "auto",
                                                                                            }}
                                                                                        >
                                                                                            {formatResponse(
                                                                                                promptInfo.response ??
                                                                                                    "",
                                                                                            )}
                                                                                        </Box>
                                                                                    </Box>
                                                                                )}
                                                                            </Stack>
                                                                        </Box>
                                                                    ),
                                                                )}
                                                            </Stack>
                                                        ) : (
                                                            <Stack spacing={2}>
                                                                {Boolean(
                                                                    node.filled_prompt,
                                                                ) && (
                                                                    <Box>
                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            gutterBottom
                                                                            sx={{
                                                                                display:
                                                                                    "flex",
                                                                                alignItems:
                                                                                    "center",
                                                                                gap: 1,
                                                                            }}
                                                                        >
                                                                            <Code />
                                                                            {t`chat-message-journey.prompts.filled_prompt`}
                                                                        </Typography>
                                                                        <Box
                                                                            sx={{
                                                                                p: 2,
                                                                                bgcolor:
                                                                                    "grey.50",
                                                                                borderRadius: 1,
                                                                                fontFamily:
                                                                                    "monospace",
                                                                                fontSize:
                                                                                    "0.875rem",
                                                                                whiteSpace:
                                                                                    "pre-wrap",
                                                                                overflowX:
                                                                                    "auto",
                                                                                border: "1px solid",
                                                                                borderColor:
                                                                                    "grey.200",
                                                                                maxHeight:
                                                                                    "300px",
                                                                                overflowY:
                                                                                    "auto",
                                                                            }}
                                                                        >
                                                                            {formatPrompt(
                                                                                node.filled_prompt ??
                                                                                    "",
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                )}

                                                                {/* Show raw_prompt when available */}
                                                                {Boolean(
                                                                    node.raw_prompt,
                                                                ) && (
                                                                    <Box>
                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            gutterBottom
                                                                            sx={{
                                                                                display:
                                                                                    "flex",
                                                                                alignItems:
                                                                                    "center",
                                                                                gap: 1,
                                                                            }}
                                                                        >
                                                                            <Code />
                                                                            {t`chat-message-journey.prompts.raw_prompt`}
                                                                        </Typography>
                                                                        <Box
                                                                            sx={{
                                                                                p: 2,
                                                                                bgcolor:
                                                                                    "grey.100",
                                                                                borderRadius: 1,
                                                                                fontFamily:
                                                                                    "monospace",
                                                                                fontSize:
                                                                                    "0.875rem",
                                                                                whiteSpace:
                                                                                    "pre-wrap",
                                                                                overflowX:
                                                                                    "auto",
                                                                                border: "1px solid",
                                                                                borderColor:
                                                                                    "grey.300",
                                                                                maxHeight:
                                                                                    "200px",
                                                                                overflowY:
                                                                                    "auto",
                                                                            }}
                                                                        >
                                                                            {formatPrompt(
                                                                                node.raw_prompt ??
                                                                                    "",
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                )}

                                                                {/* Show legacy prompt when no filled/raw prompts are available */}
                                                                {!Boolean(
                                                                    node.filled_prompt,
                                                                ) &&
                                                                    !Boolean(
                                                                        node.raw_prompt,
                                                                    ) &&
                                                                    Boolean(
                                                                        node.prompt,
                                                                    ) && (
                                                                        <Box>
                                                                            <Typography
                                                                                variant="subtitle2"
                                                                                gutterBottom
                                                                                sx={{
                                                                                    display:
                                                                                        "flex",
                                                                                    alignItems:
                                                                                        "center",
                                                                                    gap: 1,
                                                                                }}
                                                                            >
                                                                                <Code />
                                                                                {t`chat-message-journey.prompts.raw_prompt`}
                                                                            </Typography>
                                                                            <Box
                                                                                sx={{
                                                                                    p: 2,
                                                                                    bgcolor:
                                                                                        "grey.100",
                                                                                    borderRadius: 1,
                                                                                    fontFamily:
                                                                                        "monospace",
                                                                                    fontSize:
                                                                                        "0.875rem",
                                                                                    whiteSpace:
                                                                                        "pre-wrap",
                                                                                    overflowX:
                                                                                        "auto",
                                                                                    border: "1px solid",
                                                                                    borderColor:
                                                                                        "grey.300",
                                                                                    maxHeight:
                                                                                        "300px",
                                                                                    overflowY:
                                                                                        "auto",
                                                                                }}
                                                                            >
                                                                                {formatPrompt(
                                                                                    node.prompt ??
                                                                                        "",
                                                                                )}
                                                                            </Box>
                                                                        </Box>
                                                                    )}

                                                                {Boolean(
                                                                    node.response,
                                                                ) && (
                                                                    <Box>
                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            gutterBottom
                                                                            sx={{
                                                                                display:
                                                                                    "flex",
                                                                                alignItems:
                                                                                    "center",
                                                                                gap: 1,
                                                                            }}
                                                                        >
                                                                            <Message />
                                                                            {t`chat-message-journey.prompts.response`}
                                                                        </Typography>
                                                                        <Box
                                                                            sx={{
                                                                                p: 2,
                                                                                bgcolor:
                                                                                    "grey.50",
                                                                                borderRadius: 1,
                                                                                fontFamily:
                                                                                    "monospace",
                                                                                fontSize:
                                                                                    "0.875rem",
                                                                                whiteSpace:
                                                                                    "pre-wrap",
                                                                                overflowX:
                                                                                    "auto",
                                                                                border: "1px solid",
                                                                                borderColor:
                                                                                    "grey.200",
                                                                                maxHeight:
                                                                                    "200px",
                                                                                overflowY:
                                                                                    "auto",
                                                                            }}
                                                                        >
                                                                            {formatResponse(
                                                                                node.response ??
                                                                                    "",
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                            </Stack>
                                                        )}
                                                    </AccordionDetails>
                                                </Accordion>
                                            )
                                        })}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )
                })
            })()}
        </Box>
    )
}

export default PromptsSection
